import {useCallback, useMemo} from 'react';
import _ from 'lodash';

/**
 * Generic option interface for select components
 */
interface Option<Value> {
  label: string;
  value: Value;
}

/**
 * Helper function to convert an array of Option objects to an array of their values
 */
function toValuesArray<V>(options: readonly Option<V>[]) {
  return options.map((o) => o.value);
}

/**
 * Custom hook for managing multi-group select functionality
 *
 * This hook handles the logic for:
 * - Tracking available options across multiple select groups
 * - Managing option group changes
 * - Adding and removing option groups
 *
 * @param allOptions All available options to choose from
 * @param selectedOptions Currently selected options in each group
 * @param onChange Callback when selections change
 * @param maxGroups Maximum number of groups allowed
 */
export function useMultiGroupSelect<U>(
  allOptions: Option<U>[],
  selectedOptions: Option<U>[][],
  onChange: (values: U[][]) => void,
  maxGroups: number
) {
  // Validate that we don't exceed max groups
  if (selectedOptions.length > maxGroups) {
    console.error(
      `Number of filter group selects exceeds the max of ${maxGroups}`
    );
  }

  // Handle changes to a specific option group
  const onOptionGroupChanged = useCallback(
    (modifiedGroup: readonly Option<U>[], modifiedGroupIndex: number) => {
      const allUpdatedOptions = selectedOptions.map(toValuesArray);
      allUpdatedOptions[modifiedGroupIndex] = toValuesArray(modifiedGroup);
      onChange(allUpdatedOptions);
    },
    [onChange, selectedOptions]
  );

  // Handle removing an option group
  const onRemoveOptionGroup = useCallback(
    (indexToRemove: number) => {
      const updatedOptions = [...selectedOptions];
      updatedOptions.splice(indexToRemove, 1);
      onChange(updatedOptions.map(toValuesArray));
    },
    [onChange, selectedOptions]
  );

  // Handle adding a new option group
  const onAddOptionGroup = useCallback(() => {
    const updatedOptions = selectedOptions.map(toValuesArray);
    updatedOptions.push([]);
    onChange(updatedOptions);
  }, [onChange, selectedOptions]);

  // Calculate available options (options not selected in any group)
  const availableOptions = useMemo(() => {
    const allSelectedOptions = _.flatten(selectedOptions);
    return _.differenceWith(
      allOptions,
      allSelectedOptions,
      (o1, o2) => o1.value === o2.value
    );
  }, [allOptions, selectedOptions]);

  return {
    availableOptions,
    onOptionGroupChanged,
    onRemoveOptionGroup,
    onAddOptionGroup,
    canAddGroup: selectedOptions.length < maxGroups,
  };
}
